import { useEffect, useId, useRef, useState } from 'react';
import { Icon, type IconName } from '../../components/Icon';
import { useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { useDismissableOverlay } from '../../hooks/useDismissableOverlay';
import type { MoreMenuAction } from './MoreMenu';

export function ToolbarMenu({
  label,
  icon,
  actions
}: {
  readonly label: string;
  readonly icon: IconName;
  readonly actions: readonly MoreMenuAction[];
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  const focusedOnOpen = useRef(false);
  const initialFocus = useRef<'first' | 'last'>('first');
  const anchor = useAnchoredPopover(open, trigger, menu, () => 220);
  useDismissableOverlay(open, setOpen, trigger, menu);
  useEffect(() => {
    if (!open) {
      focusedOnOpen.current = false;
      return;
    }
    if (anchor && !focusedOnOpen.current) {
      const buttons = [...(menu.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
      (initialFocus.current === 'last' ? buttons.at(-1) : buttons[0])?.focus();
      focusedOnOpen.current = true;
    }
  }, [open, anchor]);
  return (
    <div className="workspace-more">
      <button
        ref={trigger}
        type="button"
        className="icon-button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => {
          initialFocus.current = 'first';
          setOpen(!open);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            initialFocus.current = event.key === 'ArrowUp' ? 'last' : 'first';
            setOpen(true);
          }
        }}
      >
        <Icon name={icon} />
      </button>
      {open && anchor ? (
        <div
          ref={menu}
          id={id}
          role="menu"
          tabIndex={-1}
          aria-label={label}
          className="workspace-more-menu"
          style={{ top: anchor.top, left: anchor.left, maxHeight: `calc(100dvh - ${anchor.top + 8}px)` }}
          onKeyDown={(event) => {
            const buttons = [...(menu.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
            const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
            let next: number;
            if (event.key === 'ArrowDown') next = (current + 1) % buttons.length;
            else if (event.key === 'ArrowUp') next = (current - 1 + buttons.length) % buttons.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = buttons.length - 1;
            else if (event.key === 'Tab') {
              trigger.current?.focus();
              setOpen(false);
              return;
            } else return;
            event.preventDefault();
            buttons[next]?.focus();
          }}
        >
          {actions.map(({ key, label: name, icon: glyph, disabled, onSelect }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              className="workspace-more-item workspace-more-action"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                trigger.current?.focus();
                onSelect();
              }}
            >
              <Icon name={glyph} size="1.1rem" />
              <span>{name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
